import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Clock, CreditCard, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { ZoneDiscovery } from "@/components/ZoneDiscovery";

const STATES = {
  pending: { icon: Clock, color: "text-amber-400", title: "Pending review", desc: "Your application has been received and is being reviewed by the Velopaths admin team. We'll email you as soon as it's approved." },
  reverted: { icon: Clock, color: "text-amber-400", title: "Back in review", desc: "Your application is being re-reviewed by the Velopaths admin team. We'll email you once a decision is made." },
  approved: { icon: CreditCard, color: "text-primary", title: "Approved — payment required", desc: "Your application is approved! Log in and complete the one-time $99 Founding Partner setup fee (first zone and one year included) to activate your partner dashboard." },
  active: { icon: CheckCircle2, color: "text-emerald-400", title: "Active", desc: "Your partner account is fully activated. Log in to manage advertisers, attractions, and your curated rider map." },
};

export default function ApplicationStatus() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const check = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/public/application-status", { params: { email: email.trim() } });
      setResult(data);
    } catch (err) {
      setResult({ found: false });
    } finally {
      setLoading(false);
    }
  };

  const st = result?.found ? STATES[result.status] : null;
  const feeStr = result?.fee != null ? `$${Number(result.fee) % 1 === 0 ? result.fee : Number(result.fee).toFixed(2)}` : "$99";

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-6">
          <div className="bg-white rounded-xl px-4 py-3 inline-flex"><img src="/velopaths-logo.png" alt="Velopaths" className="h-8 w-auto" /></div>
        </Link>
        <div className="rounded-2xl border border-border bg-card p-8">
          <div className="flex items-center gap-2 mb-1"><Search className="w-5 h-5 text-primary" /><h1 className="font-heading text-2xl font-bold">Check your application status</h1></div>
          <p className="text-sm text-muted-foreground mb-6">Enter the email you signed up with to see where your Partner application stands.</p>
          <form onSubmit={check} className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" data-testid="status-email-input" className="mt-1.5" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="status-check-button">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Check status
            </Button>
          </form>

          {result && !result.found && (
            <div className="mt-6 rounded-xl border border-border bg-secondary/40 p-4 text-center" data-testid="status-not-found">
              <XCircle className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No application found for that email. Double-check the address you used, or <Link to="/signup/shop" className="text-primary hover:underline">apply now</Link>.</p>
            </div>
          )}

          {result?.found && st && (
            <div className="mt-6 rounded-xl border border-border bg-secondary/40 p-5" data-testid="status-result">
              <div className="flex items-center gap-2 mb-1"><st.icon className={`w-5 h-5 ${st.color}`} /><h3 className="font-heading font-bold" data-testid="status-label">{st.title}</h3></div>
              {result.shop_name && <p className="text-xs text-primary mb-2">{result.shop_name}</p>}
              <p className="text-sm text-muted-foreground">{st.desc}</p>
              {result.status === "approved" && (
                <Button className="w-full mt-4" onClick={() => navigate(`/pay/setup?shop=${result.shop_id}`)} data-testid="status-pay-button"><CreditCard className="w-4 h-4 mr-2" />Pay {feeStr} & activate</Button>
              )}
              {(result.status === "approved" || result.status === "active") && (
                <Button variant="outline" className="w-full mt-2" onClick={() => navigate("/login")} data-testid="status-login-button">Log in</Button>
              )}
            </div>
          )}

          {result?.found && ["pending", "approved", "active"].includes(result.status) && (
            <div className="mt-6 rounded-xl border border-border bg-secondary/40 p-5" data-testid="status-zone-discovery">
              <ZoneDiscovery shopId={result.shop_id} email={email.trim()} authed={false} />
            </div>
          )}

          <p className="mt-5 text-sm text-center text-muted-foreground"><Link to="/login" className="text-primary hover:underline">Back to sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
